import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/ExternalServiceUserInfo.graphql';
import { Query, QueryexternalServiceUserInfoArgs } from '../../__generated__/globalTypes';

export const useExternalServiceUserInfoQuery = (
  options?: QueryHookOptions<Query, QueryexternalServiceUserInfoArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryexternalServiceUserInfoArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useExternalServiceUserInfoLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryexternalServiceUserInfoArgs>
): QueryResult<Query, QueryexternalServiceUserInfoArgs> & {
  getExternalServiceUserInfo: LazyQueryExecFunction<Query, QueryexternalServiceUserInfoArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getExternalServiceUserInfo, result] = useLazyQuery<Query, QueryexternalServiceUserInfoArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getExternalServiceUserInfo, ...result };
};

export const useExternalServiceUserInfoPromiseQuery = () => {
  const client = useApolloClient();

  const getExternalServiceUserInfo = useCallback(
    (options?: Omit<QueryOptions<QueryexternalServiceUserInfoArgs, Query>, 'query'>) =>
      client.query<Query, QueryexternalServiceUserInfoArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getExternalServiceUserInfo };
};
// Suspense Query Hooks
export const useExternalServiceUserInfoSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryexternalServiceUserInfoArgs>
) => {
  const result = useSuspenseQuery<Query, QueryexternalServiceUserInfoArgs>(QUERY, {
    ...options
  });

  return result;
};
